import md5 from 'js-md5';
import CryptoJS from 'crypto-js'
import { Base64 } from 'js-base64';
import { JSEncrypt } from 'jsencrypt'
import { sm2, sm3, sm4 } from 'sm-crypto'

// 随机字符串
const _charStr = 'abacdefghjklmnopqrstuvwxyzABCDEFGHJKLMNOPQRSTUVWXYZ0123456789';

// 加密公钥，注意保持和后端私钥配对！！
const _publicKey = 'MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQChHR+JtFykIylOI3TA1eXfDkHTE8S14VzRqbMFAMLwBJu5N1KJTRZAX0KWrXB63tSMK1LiKN5LEF80Sfj1zSMMw7IWubJHbAHlzJKE4J41oq2mC6+5K0OKVz2tFbEf6jp+FW6XzUK0cr6vCYMA+i2NYEi+2CwRU89VJXPoD+FqqwIDAQAB';

/**
 * MD5加密（不可逆算法）
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const md5Encrypt = (txtStr) => {
  let encryptData = md5(txtStr);
  return encryptData;
}

/**
 * AES加密
 * @param {*} txtStr 待加密数据
 * @param {*} keyStr 字符串key
 * @param {*} ivStr 字符串iv
 * @returns Base64加密数据
 */
export const aesEncrypt = (txtStr, keyStr, ivStr) => {
  let key = CryptoJS.enc.Utf8.parse(keyStr);
  let iv = CryptoJS.enc.Utf8.parse(ivStr);
  let srcs = CryptoJS.enc.Utf8.parse(txtStr);
  var encrypted = CryptoJS.AES.encrypt(srcs, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.ZeroPadding
  });
  return CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
}

/**
 * AES解密
 * @param {*} txtStr 待解密数据
 * @param {*} keyStr 字符串key
 * @param {*} ivStr 字符串iv
 * @returns 解密后数据
 */
export const aesDecrypt = (txtStr, keyStr, ivStr) => {
  let key = CryptoJS.enc.Utf8.parse(keyStr);
  let iv = CryptoJS.enc.Utf8.parse(ivStr);
  let base64 = CryptoJS.enc.Base64.parse(txtStr);
  let src = CryptoJS.enc.Base64.stringify(base64);
  let decrypt = CryptoJS.AES.decrypt(src, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.ZeroPadding
  });
  let decryptedStr = decrypt.toString(CryptoJS.enc.Utf8);
  return decryptedStr.toString();
}

/**
 * Base64加密
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const base64Encode = (txtStr) => {
  let encryptData = Base64.encode(txtStr);
  return encryptData;
}

/**
 * Base64解密
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const base64Decode = (txtStr) => {
  let decryptData = Base64.decode(txtStr);
  return decryptData;
}

/**
 * RSA加密（后端解密）
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export function rsaEncrypt (txtStr) {
  let encryptor = new JSEncrypt();
  let publicKey = _publicKey;
  encryptor.setPublicKey(publicKey);
  let encryptData = encryptor.encrypt(txtStr);
  return encryptData;
}

/**
 * SM2加密（后端解密）
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const sm2Encrypt = (txtStr) => {
    const cipherMode = 1 ;// 1 - C1C3C2，0 - C1C2C3，默认为1
    const publicKey = _publicKey;
    let encryptData = sm2.doEncrypt(txtStr, publicKey, cipherMode) ;
    return '04' + encryptData;
}

/**
 * SM3加密（不可逆算法）
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const sm3Encrypt = (txtStr) => {
  let encryptData = sm3(txtStr);
  return encryptData;
}

/**
 * SM4加密
 * @param {*} txtStr 待加密数据
 * @param {*} key 秘钥
 * @param {*} mode 模式：ecb、cbc, 默认ecb
 * @param {*} iv 偏移量, mode=cbc是必须有值
 * @returns 加密后数据
 */
export const sm4Encrypt = (txtStr, key, mode, iv) => {
  if (mode === 'cbc' && iv) {
    let encryptData = sm4.encrypt(txtStr, key, {'mode': mode, 'iv': iv}) ;
    return encryptData;
  }
  let encryptData = sm4.encrypt(txtStr, key) ;
  return encryptData;
}

/**
 * SM4解密
 * @param {*} txtStr 待解密数据
 * @param {*} key 私钥
 * @param {*} mode 模式：ecb、cbc, 默认ecb
 * @param {*} iv 偏移量, mode=cbc是必须有值
 * @returns 
 */
export const sm4Decrypt = (txtStr, key, mode, iv) => {
  if (mode === 'cbc' && iv) {
    let decryptData = sm4.decrypt(txtStr, key, {'mode': mode, 'iv': iv}) ;
    return decryptData;
  }
  let decryptData = sm4.decrypt(txtStr, key) ;
  return decryptData;
}

/**
 * 数据加密
 * 使用SM4对数据加密，然后对秘钥进行SM2非对称加密，最后是用SM3生成验证签名
 * 此加密方式影响性能，推荐用于部分特殊请求
 * @param {*} txtStr 待加密数据
 * @returns 加密后数据
 */
export const cryptoData = (txtStr) => {
  let key = getRandomString(16);
  let data = sm4Encrypt(JSON.stringify(txtStr), key);
  let extra = sm2Encrypt(key);
  let timestamp = new Date().getTime(); 
  let signature = sm3Encrypt(data + extra + timestamp);
  let result = {
    "data": data,
    "extra": extra,
    "timestamp": timestamp,
    "signature": signature
  }
  return result;
}

function getRandomString(len) {
	let min = 0, max = _charStr.length - 1, _str = '';
	//判断是否指定长度，否则默认长度为15
	len = len || 15;
	//循环生成字符串
	for (var i = 0, index; i < len; i++) {
		index = RandomIndex(min, max, i);
		_str += _charStr[index];
	}
	return _str;
}

function RandomIndex(min, max, i) {
	let index = Math.floor(Math.random() * (max - min + 1) + min),
		numStart = _charStr.length - 10;
	//如果字符串第一位是数字，则递归重新获取
	if (i == 0 && index >= numStart) {
		index = RandomIndex(min, max, i);
	}
	//返回最终索引值
	return index;
}